/*
 * Streams the site's Shopify-hosted videos without loading the full files.
 *
 * Shopify keeps adaptive streaming copies (1080p / 720p / 480p in 3-second chunks) of every
 * uploaded video. Instead of the original .mp4 (50-100 MB), a video tagged
 *   <video data-stream="SHOPIFY_VIDEO_ID" muted loop playsinline></video>
 * gets that stream: only the chunks being watched are downloaded, never above the quality
 * cap set for that spot.
 *
 *  - Starts loading only when the video is near the screen; pauses off screen and in hidden tabs.
 *  - data-max="480" | "720" (default 720) caps the quality; phones are capped at 480p.
 *  - Add data-stream-manual to start it yourself with MXStream.observe(video).
 *  - Reduced-motion visitors get the first frame, held still. Data-saver visitors get nothing.
 *  - Safari plays the stream natively; other browsers load hls.js (light build) once, on demand.
 *  - Fires "mx-ready" on the video and adds .is-ready when its first frame is in.
 */
(() => {
  const HLS_JS = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.light.min.js';
  const conn = navigator.connection || {};
  const saveData = !!conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let hlsLib = null;

  function loadHls() {
    return hlsLib || (hlsLib = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = HLS_JS; s.async = true;
      s.onload = () => resolve(window.Hls); s.onerror = reject;
      document.head.appendChild(s);
    }));
  }

  async function attach(v) {
    if (v._mxAttached) return;
    v._mxAttached = true;
    const id = v.dataset.stream;
    const m3u8 = `https://cdn.shopify.com/videos/c/vp/${id}/${id}.m3u8`;
    const mp4 = `https://cdn.shopify.com/videos/c/o/v/${id}.mp4`;
    v.muted = true; v.playsInline = true; v.loop = true;
    v.addEventListener('loadeddata', () => {
      v.classList.add('is-ready');
      v.dispatchEvent(new Event('mx-ready', { bubbles: true }));
    }, { once: true });

    if (v.canPlayType('application/vnd.apple.mpegurl')) {
      v.src = m3u8;                      // Safari / iOS: native streaming
    } else {
      try {
        const Hls = await loadHls();
        if (Hls && Hls.isSupported()) {
          const hls = new Hls({
            startLevel: 0,               // start on the smallest version, step up if the connection allows
            maxBufferLength: 8,          // keep only a few seconds ahead
            maxMaxBufferLength: 16,
            backBufferLength: 8,
          });
          // phones: 480p is plenty for a small screen and saves data
          const maxH = Math.min(+(v.dataset.max || 720), innerWidth < 900 ? 480 : 1080);
          hls.on(Hls.Events.MANIFEST_PARSED, (e, data) => {
            // hard ceiling: the highest version at or under data-max
            let cap = -1;
            data.levels.forEach((l, i) => { if (l.height <= maxH && (cap < 0 || l.height >= data.levels[cap].height)) cap = i; });
            if (cap >= 0) hls.autoLevelCapping = cap;
          });
          hls.loadSource(m3u8);
          hls.attachMedia(v);
          v._mxHls = hls;
        } else v.src = mp4;
      } catch (e) { v.src = mp4; }
    }
    if (calm) { v.pause(); } else if (v._mxVisible !== false) v.play().catch(() => {});
  }

  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    const v = e.target;
    v._mxVisible = e.isIntersecting;
    if (e.isIntersecting) {
      attach(v);
      if (!calm && !document.hidden && v._mxAttached) v.play().catch(() => {});
    } else if (v._mxAttached) v.pause();
  }), { rootMargin: '200px 0px' });

  function observe(v) { if (!saveData && v && v.dataset.stream) io.observe(v); }

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('video[data-stream]').forEach((v) => {
      if (!v._mxAttached) return;
      if (document.hidden) v.pause();
      else if (v._mxVisible && !calm) v.play().catch(() => {});
    });
  });

  function init() { document.querySelectorAll('video[data-stream]:not([data-stream-manual])').forEach(observe); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  window.MXStream = { observe };
})();
